import { isRight } from 'fp-ts/Either';
import { chainW, TaskEither } from 'fp-ts/lib/TaskEither';
import { AnimatePresence, motion, Variants } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { z } from 'zod';

import { usePageTitle } from '../../hooks/usePageTitle';
import {
  SignInData,
  SignUpData,
  useSubmitAuthForm,
} from '../../hooks/useSubmitAuthForm';
import { TypedError } from '../../lib/typedError';
import { getSafeFromLocation } from '../../utils/navigation';
import * as Form from '../Form';
import { LogoFull } from '../LogoFull';
import { TwoPanelsWithSeekerHero } from '../TwoPanelsWithSeekerHero';

import { FormType, FormTypeSwitch } from './FormTypeSwitch';
import { LegalCheckbox } from './LegalCheckbox';

interface Props {
  className?: string;
}

const expandVariants: Variants = {
  hidden: {
    height: 0,
    opacity: 0,
    scale: 0.95,
    y: -10,
  },
  visible: (delay = 0) => ({
    height: 'auto',
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      height: {
        duration: 0.3,
        ease: [0.32, 0.72, 0, 1],
      },
      opacity: {
        duration: 0.2,
        ease: 'easeOut',
        delay: delay + 0.05,
      },
      scale: {
        duration: 0.2,
        ease: [0.32, 0.72, 0, 1],
        delay: delay + 0.05,
      },
      y: {
        duration: 0.2,
        ease: [0.32, 0.72, 0, 1],
        delay: delay + 0.05,
      },
    },
  }),
  exit: {
    height: 0,
    opacity: 0,
    scale: 0.95,
    y: -10,
    transition: {
      height: {
        duration: 0.2,
        ease: [0.32, 0.72, 0, 1],
      },
      opacity: {
        duration: 0.15,
        ease: 'easeIn',
      },
      scale: {
        duration: 0.15,
        ease: 'easeIn',
      },
      y: {
        duration: 0.15,
        ease: 'easeIn',
      },
    },
  },
};

const slideVariants: Variants = {
  enter: (direction: 'left' | 'right') => ({
    x: direction === 'left' ? -20 : 20,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: [0.32, 0.72, 0, 1],
    },
  },
  exit: (direction: 'left' | 'right') => ({
    x: direction === 'left' ? 20 : -20,
    opacity: 0,
    transition: {
      duration: 0.2,
      ease: 'easeIn',
    },
  }),
};

const getFormSchema = (type: FormType) => {
  const baseSchema = {
    email: z.string().email('Invalid email address'),
    password: z.string(),
  };

  return type === 'signin'
    ? z.object(baseSchema)
    : z.object({
        ...baseSchema,
        fullName: z.string(),
        passwordRepeat: z.string(),
      });
};

export function AuthPage(props: Props) {
  const location = useLocation();
  const initialType =
    location.pathname === '/login' || location.state?.authFormType === 'signin'
      ? 'signin'
      : 'signup';
  const [type, setType] = useState<FormType>(initialType);
  const [passwordValue, setPasswordValue] = useState('');
  const [prevType, setPrevType] = useState<FormType>(initialType);
  const [hasInteracted, setHasInteracted] = useState(false);
  const navigate = useNavigate();
  const { submit } = useSubmitAuthForm();

  usePageTitle(
    type === 'signin'
      ? 'Sign In | Publishing Portal'
      : 'Sign Up | Publishing Portal',
  );

  const isGoingToSignIn = prevType === 'signup' && type === 'signin';
  const isGoingToSignUp = prevType === 'signin' && type === 'signup';

  const handleFormSubmit = (
    dataEither: TaskEither<
      TypedError<'InvalidFormData'>,
      SignInData | SignUpData
    >,
  ) => {
    return chainW((data: SignInData | SignUpData) => {
      return () =>
        submit(type, data).then((result) => {
          if (isRight(result)) {
            navigate(getSafeFromLocation(location.state?.from) ?? '/');
          }
          return result;
        });
    })(dataEither);
  };

  const handleTypeChange = (newType: FormType) => {
    const nextState =
      typeof location.state === 'object' && location.state !== null
        ? { ...location.state, authFormType: newType }
        : { authFormType: newType };

    setHasInteracted(true);
    setPrevType(type);
    setType(newType);
    setPasswordValue('');
    navigate(newType === 'signin' ? '/login' : '/auth', {
      state: nextState,
    });
  };

  useEffect(() => {
    setType(initialType);
    setPrevType(initialType);
    setPasswordValue('');
  }, [initialType]);

  return (
    <TwoPanelsWithSeekerHero className={props.className}>
      <div className="grid place-items-center h-full">
        <Form.Root
          className="flex flex-col items-center w-[90%] md:w-[70%]"
          formDataShape={getFormSchema(type)}
          validityKey={type}
          onSubmitData={handleFormSubmit}
        >
          <div className="lg:hidden mb-8">
            <LogoFull className="h-[24px]" type="light" />
          </div>
          <div className="text-4xl font-medium text-white text-center">
            <div className="relative h-[50px] flex items-center justify-center">
              <AnimatePresence
                mode="wait"
                initial={false}
                custom={{ isGoingToSignIn, isGoingToSignUp }}
              >
                {type === 'signup' ? (
                  <motion.div
                    key="become"
                    variants={slideVariants}
                    initial={hasInteracted ? 'enter' : false}
                    animate="center"
                    exit="exit"
                    custom={isGoingToSignUp ? 'right' : 'left'}
                    className="absolute whitespace-nowrap"
                  >
                    Become a
                  </motion.div>
                ) : (
                  <motion.div
                    key="welcome"
                    variants={slideVariants}
                    initial={hasInteracted ? 'enter' : false}
                    animate="center"
                    exit="exit"
                    custom={isGoingToSignIn ? 'right' : 'left'}
                    className="absolute whitespace-nowrap"
                  >
                    Welcome back
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            {type === 'signup' ? (
              <div className="leading-tight">Solana dApp Store developer</div>
            ) : null}
          </div>
          <Form.Input
            required
            className="w-full mt-12"
            label="Email Address"
            name="email"
            placeholder=" "
          />
          <AnimatePresence initial={false}>
            {type === 'signup' && (
              <motion.div
                key="fullname"
                variants={expandVariants}
                initial={hasInteracted ? 'hidden' : false}
                animate="visible"
                exit="exit"
                custom={0}
                className="w-full overflow-hidden"
              >
                <Form.Input
                  required
                  className="w-full mt-6"
                  label="Full Name"
                  name="fullName"
                  placeholder=" "
                />
              </motion.div>
            )}
          </AnimatePresence>
          <Form.Input
            required
            className="w-full mt-6"
            label="Password"
            name="password"
            type="password"
            placeholder=" "
            onChange={(e) => setPasswordValue(e.target.value)}
            autoComplete="new-password"
          />
          <AnimatePresence initial={false}>
            {type === 'signup' && passwordValue.length > 0 && (
              <motion.div
                key="confirm-password"
                variants={expandVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                custom={0.05}
                className="w-full overflow-hidden"
              >
                <Form.Input
                  required={passwordValue.length > 0}
                  className="w-full mt-6"
                  label="Confirm Password"
                  name="passwordRepeat"
                  type="password"
                  placeholder=" "
                />
              </motion.div>
            )}
          </AnimatePresence>
          <AnimatePresence initial={false}>
            {type === 'signin' && (
              <motion.div
                key="forgot-password"
                initial={{
                  height: 0,
                  opacity: 0,
                  marginTop: 0,
                }}
                animate={{
                  height: 'auto',
                  opacity: 1,
                  marginTop: 16,
                  transition: {
                    height: {
                      duration: 0.3,
                      ease: [0.32, 0.72, 0, 1],
                    },
                    opacity: {
                      duration: 0.25,
                      ease: 'easeOut',
                      delay: 0.1,
                    },
                    marginTop: {
                      duration: 0.3,
                      ease: [0.32, 0.72, 0, 1],
                    },
                  },
                }}
                exit={{
                  height: 0,
                  opacity: 0,
                  marginTop: 0,
                  transition: {
                    height: {
                      duration: 0.25,
                      ease: [0.32, 0.72, 0, 1],
                    },
                    opacity: {
                      duration: 0.15,
                      ease: 'easeIn',
                    },
                    marginTop: {
                      duration: 0.25,
                      ease: [0.32, 0.72, 0, 1],
                    },
                  },
                }}
                className="w-full text-right overflow-hidden"
              >
                <a
                  href="/forgot-password"
                  className="text-xs text-white underline hover:no-underline inline-block"
                >
                  Forgot password?
                </a>
              </motion.div>
            )}
          </AnimatePresence>
          <AnimatePresence initial={false}>
            {type === 'signup' && (
              <motion.div
                key="legal-checkbox"
                variants={expandVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                custom={0.1}
                className="w-full overflow-hidden"
              >
                <LegalCheckbox className="mt-6" />
              </motion.div>
            )}
          </AnimatePresence>
          <div className="mt-12 w-full relative">
            <Form.Submit className="w-full" type="submit">
              Continue
            </Form.Submit>
            <Form.SubmissionError />
          </div>
          <FormTypeSwitch
            className="mt-6"
            type={type}
            onChange={handleTypeChange}
          />
        </Form.Root>
      </div>
    </TwoPanelsWithSeekerHero>
  );
}
